import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  TrendingUp,
  Package,
  DollarSign,
  Info,
  FileText,
  Wrench,
  Recycle,
  Laptop,
  GlassWater,
  Shirt
} from 'lucide-react';
import apiService from '../../../store/api/apiService';

const PriceList = () => {
  const [priceList, setPriceList] = useState([]);

  const headerRef = useRef(null);
  const contentRef = useRef(null);

  const headerInView = useInView(headerRef, { once: true });
  const contentInView = useInView(contentRef, { once: true });

  // =====================================================
  // Default Price List Data
  // =====================================================

  const defaultPriceList = [
    {
      id: 1,
      category: 'Paper',
      item: 'Newspaper',
      unit: 'kg',
      price: 12,
      description: 'Clean newspaper'
    },
    {
      id: 2,
      category: 'Paper',
      item: 'Cardboard',
      unit: 'kg',
      price: 8,
      description: 'Clean cardboard boxes'
    },
    {
      id: 3,
      category: 'Paper',
      item: 'White Paper',
      unit: 'kg',
      price: 15,
      description: 'Office paper, A4 sheets'
    },
    {
      id: 4,
      category: 'Metal',
      item: 'Iron/Steel',
      unit: 'kg',
      price: 25,
      description: 'Iron and steel items'
    },
    {
      id: 5,
      category: 'Metal',
      item: 'Aluminum',
      unit: 'kg',
      price: 80,
      description: 'Aluminum cans, utensils'
    },
    {
      id: 6,
      category: 'Metal',
      item: 'Copper',
      unit: 'kg',
      price: 450,
      description: 'Copper wires, pipes'
    },
    {
      id: 7,
      category: 'Plastic',
      item: 'PET Bottles',
      unit: 'kg',
      price: 20,
      description: 'Plastic bottles'
    },
    {
      id: 8,
      category: 'Plastic',
      item: 'Mixed Plastic',
      unit: 'kg',
      price: 15,
      description: 'Various plastic items'
    },
    {
      id: 9,
      category: 'E-Waste',
      item: 'Mobile Phones',
      unit: 'piece',
      price: 50,
      description: 'Old mobile phones'
    },
    {
      id: 10,
      category: 'E-Waste',
      item: 'Laptops',
      unit: 'piece',
      price: 200,
      description: 'Old laptops'
    },
    {
      id: 11,
      category: 'E-Waste',
      item: 'TV/Monitor',
      unit: 'piece',
      price: 100,
      description: 'Old TVs and monitors'
    },
    {
      id: 12,
      category: 'Glass',
      item: 'Glass Bottles',
      unit: 'kg',
      price: 5,
      description: 'Clean glass bottles'
    },
    {
      id: 13,
      category: 'Textile',
      item: 'Cotton Clothes',
      unit: 'kg',
      price: 10,
      description: 'Old cotton clothes'
    },
    {
      id: 14,
      category: 'Textile',
      item: 'Mixed Clothes',
      unit: 'kg',
      price: 8,
      description: 'Mixed textile items'
    }
  ];

  // =====================================================
  // Fetch Scrap Rates
  // =====================================================

  useEffect(() => {
    let isMounted = true;

    const fetchRates = async () => {
      try {
        const data = await apiService.getScrapRates();

        // Normalize API -> UI fields
        const normalized = Array.isArray(data)
          ? data.map((d, idx) => ({
              id: d.id || d._id || idx,
              category: d.category || 'Misc',
              item: d.name || d.item || 'Item',
              unit: d.unit || 'kg',
              price: d.price ?? 0,
              description: d.description || '',
              active: d.active !== false
            }))
          : [];

        if (isMounted && normalized.length) {
          setPriceList(normalized);

          localStorage.setItem(
            'priceList',
            JSON.stringify(normalized)
          );

          return;
        }
      } catch (e) {
        // Ignore error and use fallback data
      }

      // =================================================
      // Fallback Data
      // =================================================

      const savedPriceList =
        localStorage.getItem('priceList');

      if (isMounted) {
        if (savedPriceList) {
          setPriceList(
            JSON.parse(savedPriceList)
          );
        } else {
          setPriceList(defaultPriceList);

          localStorage.setItem(
            'priceList',
            JSON.stringify(defaultPriceList)
          );
        }
      }
    };

    fetchRates();

    return () => {
      isMounted = false;
    };
  }, [defaultPriceList]);

  // =====================================================
  // Categories
  // =====================================================

  const categories = [
    ...new Set(
      priceList.map((item) => item.category)
    )
  ];

  // =====================================================
  // Category Icons
  // =====================================================

  const getCategoryIcon = (category) => {
    const icons = {
      Paper: FileText,
      Metal: Wrench,
      Plastic: Recycle,
      'E-Waste': Laptop,
      Glass: GlassWater,
      Textile: Shirt
    };

    return icons[category] || Package;
  };

  // =====================================================
  // Category Colors
  // =====================================================

  const getCategoryColor = (category) => {
    const colors = {
      Paper:
        'from-[#102018] to-[#2F6F45]',

      Metal:
        'from-[#102018] to-[#2F6F45]',

      Plastic:
        'from-[#2F6F45] to-[#70BD7D]',

      'E-Waste':
        'from-[#102018] to-[#2F6F45]',

      Glass:
        'from-[#2F6F45] to-[#70BD7D]',

      Textile:
        'from-[#102018] to-[#2F6F45]'
    };

    return (
      colors[category] ||
      'from-[#2F6F45] to-[#70BD7D]'
    );
  };

  return (
    <div className="min-h-screen bg-[#F6F8F4]">

      {/* =====================================================
          HEADER SECTION
      ====================================================== */}

      <section
        ref={headerRef}
        className="section-padding bg-white border-b border-[#DDE8DF]"
      >
        <div className="container-custom">

          <motion.div
            initial={{
              opacity: 0,
              y: 20
            }}
            animate={
              headerInView
                ? {
                    opacity: 1,
                    y: 0
                  }
                : {}
            }
            transition={{
              duration: 0.8
            }}
            className="text-center mb-12"
          >

            {/* Header Icon */}

            <div className="flex items-center justify-center mb-6">

              <div className="w-16 h-16 bg-gradient-to-br from-[#102018] to-[#2F6F45] rounded-2xl flex items-center justify-center shadow-lg shadow-[#2F6F45]/20">

                <TrendingUp
                  className="w-8 h-8 text-[#70BD7D]"
                  strokeWidth={2}
                />

              </div>

            </div>

            {/* Heading */}

            <h1 className="text-4xl md:text-5xl font-heading font-bold text-[#102018] mb-4">

              Current{' '}

              <span className="bg-gradient-to-r from-[#2F6F45] to-[#70BD7D] bg-clip-text text-transparent">
                Scrap Rates
              </span>

            </h1>

            {/* Description */}

            <p className="text-xl text-[#3F4D45] max-w-3xl mx-auto leading-relaxed">
              Get the best market rates for your scrap materials.
              Prices are updated regularly based on current market conditions.
            </p>

          </motion.div>

          {/* =================================================
              STATS CARDS
          ================================================== */}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">

            {[
              {
                icon: Package,
                label: 'Categories',
                value: categories.length,
                color:
                  'from-[#102018] to-[#2F6F45]'
              },
              {
                icon: DollarSign,
                label: 'Items Listed',
                value: priceList.length,
                color:
                  'from-[#2F6F45] to-[#70BD7D]'
              },
              {
                icon: TrendingUp,
                label: 'Market Rates',
                value: 'Live',
                color:
                  'from-[#102018] to-[#2F6F45]'
              }
            ].map((stat, index) => {

              const StatIcon = stat.icon;

              return (
                <motion.div
                  key={index}
                  className="bg-white border border-[#DDE8DF] rounded-2xl p-6 text-center shadow-sm hover:shadow-lg hover:shadow-[#2F6F45]/10 transition-all duration-300"
                  initial={{
                    opacity: 0,
                    y: 20
                  }}
                  animate={
                    headerInView
                      ? {
                          opacity: 1,
                          y: 0
                        }
                      : {}
                  }
                  transition={{
                    duration: 0.5,
                    delay: 0.2 + index * 0.1
                  }}
                  whileHover={{
                    scale: 1.03
                  }}
                >

                  {/* Stat Icon */}

                  <div
                    className={`w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-r ${stat.color} flex items-center justify-center`}
                  >

                    <StatIcon
                      className="w-6 h-6 text-[#70BD7D]"
                      strokeWidth={2}
                    />

                  </div>

                  <h3 className="text-2xl font-heading font-bold text-[#102018] mb-2">
                    {stat.value}
                  </h3>

                  <p className="text-[#3F4D45]">
                    {stat.label}
                  </p>

                </motion.div>
              );
            })}

          </div>

        </div>
      </section>

      {/* =====================================================
          PRICE LIST CONTENT
      ====================================================== */}

      <section
        ref={contentRef}
        className="section-padding"
      >

        <div className="container-custom">

          <div className="space-y-12">

            {categories.map(
              (category, categoryIndex) => {

                const CategoryIcon =
                  getCategoryIcon(category);

                return (
                  <motion.div
                    key={category}
                    initial={{
                      opacity: 0,
                      y: 20
                    }}
                    animate={
                      contentInView
                        ? {
                            opacity: 1,
                            y: 0
                          }
                        : {}
                    }
                    transition={{
                      duration: 0.5,
                      delay: categoryIndex * 0.1
                    }}
                    className="bg-white border border-[#DDE8DF] rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow duration-300"
                  >

                    {/* Category Header */}

                    <div className="flex items-center mb-8">

                      <div
                        className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${getCategoryColor(category)} flex items-center justify-center mr-4 shadow-md shadow-[#2F6F45]/10`}
                      >

                        <CategoryIcon
                          className="w-8 h-8 text-[#70BD7D]"
                          strokeWidth={2}
                        />

                      </div>

                      <div>

                        <h2 className="text-2xl font-heading font-bold text-[#102018]">
                          {category}
                        </h2>

                        <p className="text-[#3F4D45]">
                          Current rates for{' '}
                          {category.toLowerCase()}{' '}
                          items
                        </p>

                      </div>

                    </div>

                    {/* Items Grid */}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

                      {priceList
                        .filter(
                          (item) =>
                            item.category === category
                        )
                        .map(
                          (item, itemIndex) => (

                            <motion.div
                              key={item.id}
                              className="bg-[#F6F8F4] border border-[#DDE8DF] rounded-xl p-6 hover:shadow-lg hover:shadow-[#2F6F45]/10 hover:border-[#70BD7D] transition-all duration-300"
                              initial={{
                                opacity: 0,
                                scale: 0.9
                              }}
                              animate={
                                contentInView
                                  ? {
                                      opacity: 1,
                                      scale: 1
                                    }
                                  : {}
                              }
                              transition={{
                                duration: 0.3,
                                delay:
                                  categoryIndex *
                                    0.1 +
                                  itemIndex *
                                    0.05
                              }}
                              whileHover={{
                                scale: 1.02
                              }}
                            >

                              <div className="flex items-start justify-between mb-4">

                                <h3 className="text-lg font-heading font-semibold text-[#102018]">
                                  {item.item}
                                </h3>

                                <div className="text-right">

                                  <div className="text-2xl font-bold text-[#2F6F45]">
                                    ₹{item.price}
                                  </div>

                                  <div className="text-sm text-[#3F4D45]">
                                    per {item.unit}
                                  </div>

                                </div>

                              </div>

                              <p className="text-[#3F4D45] text-sm leading-relaxed">
                                {item.description}
                              </p>

                            </motion.div>

                          )
                        )}

                    </div>

                  </motion.div>
                );
              }
            )}

          </div>

        </div>
      </section>

      {/* =====================================================
          DISCLAIMER
      ====================================================== */}

      <section className="section-padding bg-white border-t border-[#DDE8DF]">

        <div className="container-custom">

          <motion.div
            initial={{
              opacity: 0,
              y: 20
            }}
            whileInView={{
              opacity: 1,
              y: 0
            }}
            transition={{
              duration: 0.8
            }}
            viewport={{
              once: true
            }}
            className="bg-gradient-to-r from-[#EAF5EC] to-white border border-[#DDE8DF] rounded-2xl p-6 md:p-8 shadow-sm"
          >

            <div className="flex items-start space-x-4">

              {/* Info Icon */}

              <div className="w-12 h-12 bg-[#EAF5EC] border border-[#DDE8DF] rounded-xl flex items-center justify-center flex-shrink-0">

                <Info
                  className="w-6 h-6 text-[#2F6F45]"
                  strokeWidth={2}
                />

              </div>

              <div>

                <h3 className="text-lg font-heading font-semibold text-[#102018] mb-2">
                  Important Note
                </h3>

                <p className="text-[#3F4D45] leading-relaxed">

                  <strong className="text-[#102018]">
                    Prices are subject to market fluctuations
                  </strong>{' '}

                  and may vary based on quality and quantity.
                  Final prices will be determined during pickup after
                  inspection by our professional team.
                  We ensure fair and transparent pricing for all our customers.

                </p>

              </div>

            </div>

          </motion.div>

        </div>
      </section>

    </div>
  );
};

export default PriceList;